import React, { useState } from 'react';

/**
 * ExportOptionsModal - Reusable modal for export options
 * 
 * Features:
 * - Radio button selection for export type
 * - Configurable options
 * - Loading state during export
 * - Custom descriptions
 * 
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether modal is open
 * @param {Function} props.onClose - Close handler
 * @param {Function} props.onExport - Export handler (receives selected export type)
 * @param {boolean} props.isExporting - Whether export is in progress
 * @param {string} props.title - Modal title
 * @param {Array} props.options - Export options [{ value, title, description }]
 * @param {string} props.defaultOption - Default selected option value
 * @param {string} props.accentColor - Accent color for buttons
 */
const ExportOptionsModal = ({
  isOpen,
  onClose,
  onExport,
  isExporting = false,
  title = 'Export Data',
  options = null,
  defaultOption = 'frontend',
  accentColor = '#28a745',
}) => {
  // Default options if none provided
  const defaultOptions = [
    {
      value: 'frontend',
      title: 'Dashboard Columns Only',
      description: 'Export only the columns visible in the dashboard',
    },
    {
      value: 'all-editable',
      title: 'All Editable Columns',
      description: 'Export all editable columns, excluding auto-calculated fields',
    },
    {
      value: 'all',
      title: 'All Columns',
      description: 'Export all columns including calculated fields and timestamps',
    },
  ];

  const exportOptions = options || defaultOptions;
  const [exportType, setExportType] = useState(defaultOption);

  const handleSubmit = () => {
    onExport(exportType);
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 10000,
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        padding: '2rem',
        width: '90%',
        maxWidth: '550px',
        maxHeight: '90vh',
        overflowY: 'auto',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1.5rem',
        }}>
          <h3 style={{ margin: 0, color: '#333' }}>
            📊 {title}
          </h3>
          <button
            onClick={onClose}
            disabled={isExporting}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '1.5rem',
              cursor: isExporting ? 'not-allowed' : 'pointer',
              color: '#666',
              padding: '0.25rem',
              opacity: isExporting ? 0.5 : 1,
            }}
          >
            ×
          </button>
        </div>

        {/* Info Box */}
        <div style={{
          backgroundColor: '#e3f2fd',
          padding: '1rem',
          borderRadius: '4px',
          marginBottom: '1.5rem',
          fontSize: '0.9rem',
          color: '#1976d2',
        }}>
          <strong>Export Options:</strong>
          <p style={{ margin: '0.5rem 0 0 0' }}>
            Choose which columns to include in your Excel export.
          </p>
        </div>

        {/* Options */}
        <div style={{ marginBottom: '1.5rem' }}>
          {exportOptions.map((option) => (
            <label
              key={option.value}
              style={{
                display: 'flex',
                alignItems: 'center',
                padding: '1rem',
                border: `2px solid ${exportType === option.value ? accentColor : '#dee2e6'}`,
                borderRadius: '4px',
                marginBottom: '0.75rem',
                cursor: isExporting ? 'not-allowed' : 'pointer',
                backgroundColor: exportType === option.value ? `${accentColor}10` : 'white',
                transition: 'all 0.2s ease',
                opacity: isExporting ? 0.7 : 1,
              }}
            >
              <input
                type="radio"
                name="exportType"
                value={option.value}
                checked={exportType === option.value}
                onChange={(e) => setExportType(e.target.value)}
                disabled={isExporting}
                style={{
                  marginRight: '1rem',
                  cursor: isExporting ? 'not-allowed' : 'pointer',
                  width: '18px',
                  height: '18px',
                }}
              />
              <div>
                <div style={{
                  fontWeight: 'bold',
                  marginBottom: '0.25rem',
                  color: '#333',
                }}>
                  {option.title}
                </div>
                <div style={{ fontSize: '0.85rem', color: '#666' }}>
                  {option.description}
                </div>
              </div>
            </label>
          ))}
        </div>

        {/* Footer Buttons */}
        <div style={{
          display: 'flex',
          gap: '1rem',
          justifyContent: 'flex-end',
        }}>
          <button
            onClick={onClose}
            disabled={isExporting}
            style={{
              padding: '0.75rem 1.5rem',
              backgroundColor: '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: isExporting ? 'not-allowed' : 'pointer',
              fontSize: '1rem',
              opacity: isExporting ? 0.5 : 1,
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={isExporting}
            style={{
              padding: '0.75rem 1.5rem',
              backgroundColor: isExporting ? '#6c757d' : accentColor,
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: isExporting ? 'not-allowed' : 'pointer',
              fontSize: '1rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
            }}
          >
            {isExporting ? (
              <>
                <div style={{
                  width: '16px',
                  height: '16px',
                  border: '2px solid #fff',
                  borderTop: '2px solid transparent',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite',
                }}></div>
                Exporting...
              </>
            ) : (
              <>📊 Export to Excel</>
            )}
          </button>
        </div>

        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    </div>
  );
};

export default ExportOptionsModal;
